import React, { useEffect, useRef, useState } from 'react';
import moment from 'moment';
import { EVENT_TYPES, NAVIGATION_PRESETS, REPORT_ACTIONS } from './features/registry';
import './TopBar.css';
import { t } from './i18n';

const FILTER_ICONS = {
  checkin: '📍',
  walking: '🚶',
  running: '🏃',
  cycling: '🚴',
  transport: '🚌',
  car: '🚗',
  health: '💚',
  google_maps: '🗺️',
  movie: '🎬',
  shows: '📺',
  concerts: '🎸',
  spotify: '🎧',
  apple_music: '🎵',
  diary: '📓',
};

const PERIOD_PRESETS = [
  { label: 'Hoje', key: 'today' },
  { label: '7d', key: '7d' },
  { label: '30d', key: '30d' },
  { label: '3m', key: '3m' },
  { label: '6m', key: '6m' },
  { label: '1a', key: 'year' },
  { label: 'Tudo', key: 'all' },
];

const CALENDAR_VIEW_OPTIONS = [
  { labelKey: 'web.topbar.month', view: 'month' },
];

const CALENDAR_VIEW_KEYS = CALENDAR_VIEW_OPTIONS.map(option => option.view);

function getPresetDates(key) {
  const today = moment();
  switch (key) {
    case 'today':
      return { startDate: today.format('YYYY-MM-DD'), endDate: today.format('YYYY-MM-DD') };
    case '7d':
      return { startDate: today.clone().subtract(7, 'days').format('YYYY-MM-DD'), endDate: today.format('YYYY-MM-DD') };
    case '30d':
      return { startDate: today.clone().subtract(30, 'days').format('YYYY-MM-DD'), endDate: today.format('YYYY-MM-DD') };
    case '3m':
      return { startDate: today.clone().subtract(3, 'months').format('YYYY-MM-DD'), endDate: today.format('YYYY-MM-DD') };
    case '6m':
      return { startDate: today.clone().subtract(6, 'months').format('YYYY-MM-DD'), endDate: today.format('YYYY-MM-DD') };
    case 'month':
      return { startDate: today.clone().startOf('month').format('YYYY-MM-DD'), endDate: today.clone().endOf('month').format('YYYY-MM-DD') };
    case 'year':
      return { startDate: today.clone().subtract(1, 'year').format('YYYY-MM-DD'), endDate: today.format('YYYY-MM-DD') };
    case 'all':
      return { startDate: '2010-01-01', endDate: today.format('YYYY-MM-DD') };
    default:
      return {};
  }
}

function detectActivePreset(startDate, endDate) {
  for (const preset of PERIOD_PRESETS) {
    const dates = getPresetDates(preset.key);
    if (dates.startDate === startDate && dates.endDate === endDate) {
      return preset.key;
    }
  }
  return null;
}

function TopBar({
  currentFilters,
  onFilterChange,
  onOpenDrawer,
  onOpenImport,
  onOpenAnniversary,
  onOpenStats,
  onOpenDashboard,
  onOpenGithub,
  onOpenMemoryBook,
  onOpenSearch,
  activeNavigationPresetKey,
  activeNavigationPreset,
  hasActivePresetResults = true,
  onNavigationPresetSelect,
  onPresetWidenPeriod,
  calendarDate,
  calendarView,
  onCalendarNavigate,
  user,
  onLogout,
}) {
  const [isMoreMenuOpen, setIsMoreMenuOpen] = useState(false);
  const moreMenuRef = useRef(null);
  const moreButtonRef = useRef(null);
  const { startDate, endDate, selectedTypes } = currentFilters;
  const activePreset = detectActivePreset(startDate, endDate);
  const currentMonthLabel = moment(calendarDate).format('MMMM YYYY');

  const handlePreset = (key) => {
    const dates = getPresetDates(key);
    onFilterChange({
      ...currentFilters,
      ...dates,
    });
  };

  const handleToggleType = (typeKey) => {
    const isActive = selectedTypes.includes(typeKey);
    const nextTypes = isActive
      ? selectedTypes.filter(t => t !== typeKey)
      : [...selectedTypes, typeKey];
    onFilterChange({
      ...currentFilters,
      selectedTypes: nextTypes,
    });
  };

  const getSupportedCalendarView = () => (
    CALENDAR_VIEW_KEYS.includes(calendarView) ? calendarView : 'month'
  );

  const navigateCalendar = (direction) => {
    const view = getSupportedCalendarView();
    const nextDate = direction === 'previous'
      ? moment(calendarDate).subtract(1, 'month').toDate()
      : moment(calendarDate).add(1, 'month').toDate();
    onCalendarNavigate(nextDate, view);
  };

  const goToday = () => {
    onCalendarNavigate(moment().toDate(), getSupportedCalendarView());
  };

  const secondaryActions = [
    { key: 'anniversary', label: '🗓️ Neste dia', onClick: onOpenAnniversary },
    { key: 'stats', label: t('web.topbar.stats'), onClick: onOpenStats },
    { key: 'dashboard', label: '📈 Insights', onClick: onOpenDashboard },
    { key: 'github', label: '⌂ GitHub', onClick: onOpenGithub },
    REPORT_ACTIONS.some(action => action.featureKey === 'memory_book')
      ? { key: 'memory-book', label: '📘 Memory Book', onClick: onOpenMemoryBook }
      : null,
    user && user.role === 'admin'
      ? { key: 'import', label: t('web.topbar.import'), onClick: onOpenImport }
      : null,
  ].filter(action => action && action.onClick);

  useEffect(() => {
    if (!isMoreMenuOpen) return undefined;

    const handlePointerDown = (event) => {
      if (!moreMenuRef.current?.contains(event.target)) {
        setIsMoreMenuOpen(false);
      }
    };
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        setIsMoreMenuOpen(false);
        moreButtonRef.current?.focus();
      }
    };

    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isMoreMenuOpen]);

  const runSecondaryAction = (action) => {
    moreButtonRef.current?.focus();
    setIsMoreMenuOpen(false);
    action();
  };

  return (
    <div className="top-bar">
      <div className="top-bar-main">
        <div className="top-bar-left">
          <button type="button" className="top-bar-icon-btn" onClick={onOpenDrawer} aria-label="Abrir filtros">
            📅
          </button>
          <button type="button" className="top-bar-btn" onClick={goToday}>
            Hoje
          </button>
        </div>

        <div className="top-bar-month-nav">
          <button
            type="button"
            className="top-bar-nav-arrow"
            onClick={() => navigateCalendar('previous')}
            aria-label={t('web.calendar.prevMonth')}
          >
            ‹
          </button>
          <span className="top-bar-month-label">{currentMonthLabel}</span>
          <button
            type="button"
            className="top-bar-nav-arrow"
            onClick={() => navigateCalendar('next')}
            aria-label={t('web.calendar.nextMonth')}
          >
            ›
          </button>
        </div>

        <div className="top-bar-actions">
          {onOpenSearch && (
            <button type="button" className="top-bar-btn top-bar-search-action top-bar-primary-action" onClick={onOpenSearch}>
              {t('web.topbar.search')}
            </button>
          )}
          {secondaryActions.length > 0 && (
            <div className="top-bar-more" ref={moreMenuRef}>
              <button
                ref={moreButtonRef}
                type="button"
                className="top-bar-btn top-bar-more-toggle"
                aria-expanded={isMoreMenuOpen}
                aria-haspopup="menu"
                aria-controls="top-bar-more-menu"
                onClick={() => setIsMoreMenuOpen((isOpen) => !isOpen)}
              >
                ⋯ Mais
              </button>
              {isMoreMenuOpen ? (
                <div id="top-bar-more-menu" className="top-bar-more-menu" role="menu" aria-label={t('web.topbar.moreActions')}>
                  {secondaryActions.map((action) => (
                    <button
                      key={action.key}
                      type="button"
                      className="top-bar-more-item"
                      role="menuitem"
                      onClick={() => runSecondaryAction(action.onClick)}
                    >
                      {action.label}
                    </button>
                  ))}
                </div>
              ) : null}
            </div>
          )}
          {user && (
            <div className="top-bar-user-badge">
              <span className="user-name" title={t('web.topbar.userTitle', { user: user.username, role: user.role })}>
                👤 {user.username}
              </span>
              <button type="button" className="top-bar-logout-btn" onClick={onLogout} aria-label="Sair" title="Sair">
                🚪
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="top-bar-chips">
        <div className="top-bar-presets-row">
          {PERIOD_PRESETS.map(preset => (
            <button
              key={preset.key}
              type="button"
              className={`top-bar-range-chip ${activePreset === preset.key ? 'is-active' : ''}`}
              onClick={() => handlePreset(preset.key)}
            >
              {preset.label}
            </button>
          ))}
          <span className="top-bar-date-range">
            {startDate ? moment(startDate).format('DD/MM/YYYY') : '—'} – {endDate ? moment(endDate).format('DD/MM/YYYY') : '—'}
          </span>
        </div>

        {onNavigationPresetSelect && (
          <div className="top-bar-navigation-presets-row" aria-label={t('web.topbar.navPresets')}>
            {NAVIGATION_PRESETS.map(preset => {
              const isActive = activeNavigationPresetKey === preset.key;
              return (
                <button
                  key={preset.key}
                  type="button"
                  className={`top-bar-navigation-preset ${isActive ? 'is-active' : ''}`}
                  aria-pressed={isActive}
                  onClick={() => onNavigationPresetSelect(preset)}
                >
                  <span className="navigation-preset-icon">{preset.icon}</span>
                  <span>{preset.label}</span>
                </button>
              );
            })}
          </div>
        )}

        {activeNavigationPreset && !hasActivePresetResults && (
          <div className="top-bar-preset-empty" role="status">
            <span>{t('web.topbar.noDataPreset', { label: activeNavigationPreset.label })}</span>
            <button type="button" onClick={onPresetWidenPeriod}>
              {t('web.topbar.widenPeriod')}
            </button>
            {onOpenImport && (
              <button type="button" onClick={onOpenImport}>
                {t('action.import')}
              </button>
            )}
          </div>
        )}

        <div className="top-bar-types-row">
          {EVENT_TYPES.map(type => {
            const isActive = selectedTypes.includes(type.key);
            const icon = FILTER_ICONS[type.key] || '📍';
            return (
              <button
                key={type.key}
                type="button"
                className={`top-bar-type-chip ${isActive ? 'is-active' : ''}`}
                onClick={() => handleToggleType(type.key)}
              >
                <span className="type-chip-icon">{icon}</span>
                <span className="type-chip-label">{type.label}</span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default TopBar;
export { FILTER_ICONS, getPresetDates, detectActivePreset, PERIOD_PRESETS, CALENDAR_VIEW_KEYS, CALENDAR_VIEW_OPTIONS };
